// React
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Libraries
import BVariationSelect, { HostStyles } from './styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown as farChevronDown } from '@fortawesome/pro-regular-svg-icons'
import _sortBy from 'lodash/sortBy'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import { withTranslation } from 'shared/utils/withTranslation'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import { fetchPrice } from 'shop-components/redux/actions/price'
import { fetchVariationAvailability } from 'shop-components/redux/actions/availability'
import { Settings as SettingsUtils } from 'back_office/v2/utils/utils'

const ProductVariationsSelect = ({
  name,
  productId,
  variations,
  withAvailability,
  cart,
  fetchProductPrice,
  fetchAvailability,
  setVariation,
  setVariationError,
  error,
  t
}) => {
  const [selected, setSelected] = useState('')

  const handleSelectVariation = useCallback(
    (id) => {
      setVariation(productId, id)
      setVariationError(productId, false)
      setSelected(id)
    },
    [productId, setVariation]
  )

  useEffect(() => {
    if (!(selected && cart.id)) return

    fetchProductPrice({ id: selected, cartId: cart.id })
  }, [selected, cart.id, cart.startsAt, cart.stopsAt])

  useEffect(() => {
    if (!(productId && variations?.length && cart.startsAt && cart.stopsAt && cart.startLocationId)) return

    fetchAvailability({
      ids: variations.map(({ id }) => id),
      productId,
      startsAt: cart.startsAt,
      stopsAt: cart.stopsAt,
      startLocationId: cart.startLocationId
    })
  }, [productId, variations?.length, cart.startsAt, cart.stopsAt, cart.startLocationId])

  if (!variations || variations?.length <= 1) return null

  return (
    <Fragment>
      <HostStyles />
      <BVariationSelect>
        <BVariationSelect.Select
          className="variations-select"
          name="variation"
          value={selected}
          onChange={(e) => handleSelectVariation(e.target.value)}
          error={error}
          required
        >
          <option value="" className="placeholder">{t('user.store.select_variation_placeholder')}</option>
          {variations.map((variation) => {
            return (
              <option key={variation.id} value={variation.id}>
                {`${variation.name.replace(`${name} - `, '')}${
                  withAvailability && variation.availability ?
                    ` (${variation.availability.plannable} ${t('user.store.available')?.toLowerCase?.()})` :
                    ''
                }`}
              </option>
            )
          })}
        </BVariationSelect.Select>
        <FontAwesomeIcon icon={farChevronDown} />
        {error && <BVariationSelect.Error>{t('user.store.select_variation_error')}</BVariationSelect.Error>}
      </BVariationSelect>
    </Fragment>
  )
}

ProductVariationsSelect.propTypes = {
  productId: PropTypes.string.isRequired,
  name: PropTypes.string,
  cart: PropTypes.object,
  variations: PropTypes.array,
  withAvailability: PropTypes.bool,
  fetchProductPrice: PropTypes.func,
  fetchAvailability: PropTypes.func,
  setVariation: PropTypes.func,
  setVariationError: PropTypes.func,
  error: PropTypes.bool,
  t: PropTypes.func
}

ProductVariationsSelect.displayName = 'ProductVariationsSelect'

const mapStateToProps = (state, props) => {
  const cart = state.orm.carts.getById(state.client.cartId || window.booqableOptions.cart?.id)
  const hasDates = !!(cart?.starts_at && cart?.stops_at)

  const item = state.items.records[props.productId]
  const variations = _sortBy(item?.products, 'sorting_weight')

  const withAvailability =
    hasDates &&
    item?.tracking_type !== 'none' &&
    SettingsUtils.get('store.use_availability') &&
    !SettingsUtils.get('store.hide_product_availability_quantities')

  return {
    cart: {
      id: cart?.id ?? window.booqableOptions.cart?.id,
      startsAt: cart?.starts_at ?? window.booqableOptions.cart?.from,
      stopsAt: cart?.stops_at ?? window.booqableOptions.cart?.till,
      startLocationId: cart?.start_location_id ?? window.booqableOptions.cart?.start_location_id
    },
    name: item?.name,
    variations,
    withAvailability,
    error: item?.error || false
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchProductPrice: ({ id, cartId }) => dispatch(fetchPrice({ id, cartId })),
  fetchAvailability: (attributes) => dispatch(fetchVariationAvailability(attributes)),
  setVariation: (id, variationId) => dispatch({ type: 'PRODUCT_UPDATE_VARIATION', payload: { id, variationId } }),
  setVariationError: (id, value) => dispatch({ type: 'PRODUCT_VARIATION_ERROR', payload: { id, value } })
})

const decoratedComponent = compose(
  withStore,
  withTheme,
  withTranslation('user'),
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(ProductVariationsSelect)

registerCustomElement('bq-product-variations-select', decoratedComponent, {
  attributes: ['product-id']
})

export default decoratedComponent
